/**
 * Variables métier saisies sur la page Frais, toutes au même endroit :
 *
 *  · coût d'envoi par commande (€ HT) → Shipping costs = ce coût × commandes,
 *    sur la page Shopify (Sales KPIs, MC1', tableaux) ;
 *  · frais Shopify Payments (% du TTC encaissé + € par commande) → MC1 de la
 *    page Shopify, marge nette du canal Shopify et MC2 du Résumé.
 * Le coût de revient d'un kit, lui, se saisit par période
 * (`KitCostPeriodsEditor`).
 *
 * L'API Shopify ne donne pas le prix réel des étiquettes : l'équipe le saisit.
 * Enregistrées en base, ces valeurs valent pour tous les admins.
 */

import {
  SHIPPING_COST_MAX,
  SHOP_PAYMENT_FEE_PERCENT_MAX,
  SHOP_PAYMENT_FEE_PER_ORDER_MAX,
  type BusinessSettings,
  type BusinessSettingsInput,
} from '@shared/types/settings';
import { useEffect, useState, type FormEvent } from 'react';

import { useBusinessSettings, useSaveBusinessSettings } from '../hooks/useBusinessSettings';

type NumericKey = 'shippingCostPerOrder' | 'shopPaymentFeePercent' | 'shopPaymentFeePerOrder';

interface FieldSpec {
  key: NumericKey;
  id: string;
  label: string;
  placeholder: string;
  max: number;
  unit: string;
  hint: string;
}

const FIELDS: readonly FieldSpec[] = [
  {
    key: 'shippingCostPerOrder',
    id: 'shipping-cost',
    label: "Coût d'envoi par commande (€ HT)",
    placeholder: 'ex. 7,50',
    max: SHIPPING_COST_MAX,
    unit: '€',
    hint: '× commandes = frais d\'envoi',
  },
  {
    key: 'shopPaymentFeePercent',
    id: 'shop-payment-fee-percent',
    label: 'Frais Shopify Payments (% du CA TTC encaissé)',
    placeholder: 'ex. 1,6',
    max: SHOP_PAYMENT_FEE_PERCENT_MAX,
    unit: '%',
    hint: 'commission par transaction — MC1 Shopify, MC2 du Résumé',
  },
  {
    key: 'shopPaymentFeePerOrder',
    id: 'shop-payment-fee-per-order',
    label: 'Frais Shopify Payments fixes (€ par commande)',
    placeholder: 'ex. 0,25',
    max: SHOP_PAYMENT_FEE_PER_ORDER_MAX,
    unit: '€',
    hint: 'part fixe de la commission, 0 si aucune',
  },
];

function SettingField({ spec, settings }: { spec: FieldSpec; settings: BusinessSettings | undefined }) {
  const save = useSaveBusinessSettings();
  const stored = settings?.[spec.key] ?? null;

  const [draft, setDraft] = useState('');
  // Le champ suit la valeur enregistrée tant qu'on ne le modifie pas.
  useEffect(() => {
    setDraft(stored === null ? '' : String(stored).replace('.', ','));
  }, [stored]);

  const cleaned = draft.replace(/\s/g, '').replace(spec.unit, '').replace(',', '.');
  const parsed = cleaned === '' ? null : Number(cleaned);
  const invalid = parsed !== null && (!Number.isFinite(parsed) || parsed < 0 || parsed > spec.max);
  const unchanged = parsed === stored;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (invalid || unchanged) return;
    const input: BusinessSettingsInput = { [spec.key]: parsed };
    save.mutate(input);
  };

  return (
    <form className="filters" onSubmit={onSubmit}>
      <div className="field">
        <label className="field__label" htmlFor={spec.id}>
          {spec.label}
        </label>
        <input
          id={spec.id}
          type="text"
          inputMode="decimal"
          placeholder={spec.placeholder}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          aria-invalid={invalid}
          style={{ width: 110 }}
        />
      </div>
      <button type="submit" className="btn" disabled={invalid || unchanged || save.isPending}>
        {save.isPending ? 'Enregistrement…' : 'Enregistrer'}
      </button>
      <span className="field__label" role="status">
        {invalid
          ? `entre 0 et ${spec.max} ${spec.unit}`
          : save.error
            ? `échec : ${save.error.message}`
            : stored === null
              ? `non renseigné — ${spec.hint}`
              : spec.hint}
      </span>
    </form>
  );
}

export function ShopCostSettings({ only }: { only?: NumericKey }) {
  const settings = useBusinessSettings();
  return (
    <>
      {FIELDS.filter((spec) => !only || spec.key === only).map((spec) => (
        <SettingField key={spec.key} spec={spec} settings={settings.data} />
      ))}
    </>
  );
}
